import { TFunction, useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import WhereIsMyVinImage from "@public/images/where-is-my-vin.png";
import WhereIsMyVinGraphicSmall from "@public/images/where-is-my-vin-small.svg";
import { TranslationKeys } from "src/@types/i18next";
import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
  Modal,
} from "@talladega/components";
import LocalImage from "../../LocalImage";

const whereIsMyVinGraphic = (t: TFunction, src?: string) => {
  return (
    <div className="flex flex-col items-center py-4 mx-auto sm:w-[456px]">
      <LocalImage
        src={src ?? WhereIsMyVinImage}
        alt={t("where_is_my_vin_graphic", { ns: "common" })}
        width="341"
        priority
        className="mb-6"
      />

      <div className="w-full text-start text-primary-deep kijiji:text-gray-500">
        <h1 className="text-lg font-semibold pb-4">
          {t("you_can_find_your_vin", { ns: "common" })}
        </h1>

        <div className="flex flex-col gap-4 justify-between px-1">
          <div>
            <p className="text-md font-medium text-base pb-1">
              {t("within_the_vehicle", { ns: "common" })}:
            </p>
            <ul className="list-disc list-inside text-md text-base font-normal">
              <li>{t("drivers_side_dashboard", { ns: "common" })}</li>
              <li>{t("inside_drivers_door", { ns: "common" })}</li>
            </ul>
          </div>
          <div>
            <p className="text-md font-medium text-base pb-1">
              {t("in_documents", { ns: "common" })}:
            </p>
            <ul className="list-disc list-inside text-md font-normal text-base">
              <li>{t("ownership_document", { ns: "common" })}</li>
              <li>{t("insurance_policy", { ns: "common" })}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const whereIsMyVinGraphicSmall = (t: TFunction, src?: string) => {
  return (
    <div className="flex flex-col flex-col-reverse 2sm:flex-row items-center mx-auto">
      <div className="w-full text-start text-primary-deep kijiji:text-gray-700 font-medium pt-4 2sm:pt-0 w-full">
        <div className="flex flex-col gap-4 justify-between px-1">
          <div>
            <p className="text-md font-medium text-base pb-1">
              {t("within_the_vehicle", { ns: "common" })}:
            </p>
            <ul className="list-disc list-inside text-md text-base font-normal">
              <li>{t("drivers_side_dashboard", { ns: "common" })}</li>
              <li>{t("inside_drivers_door", { ns: "common" })}</li>
            </ul>
          </div>
          <div>
            <p className="text-md font-medium text-base pb-1">
              {t("in_documents", { ns: "common" })}:
            </p>
            <ul className="list-disc list-inside text-md font-normal text-base">
              <li>{t("ownership_document", { ns: "common" })}</li>
              <li>{t("insurance_policy", { ns: "common" })}</li>
            </ul>
          </div>
        </div>
      </div>
      <LocalImage
        className="w-3/5"
        src={src ?? WhereIsMyVinGraphicSmall}
        alt={t("where_is_my_vin_graphic", { ns: "common" })}
        priority
      />
    </div>
  );
};

type Props = {
  themeClassNames?: string;
  labelKey?: TranslationKeys["common"];
  src?: string;
};

const WhereIsMyVinGraphic = ({
  themeClassNames,
  labelKey = "common:where_is_my_vin",
  src,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInIframe, setIsInIframe] = useState<boolean>(true);

  useEffect(() => {
    setIsInIframe(window !== window.top);
  }, []);

  return (
    <>
      <div id="whereIsMyVin" className="focus:outline-non inline-block">
        <div className="justify-start flex text-primary-bold font-medium kijiji:font-normal autocan:font-normal hover:cursor-pointer">
          <div
            onClick={() => setIsModalOpen(true)}
            onKeyDown={() => setIsModalOpen(true)}
            role="button"
            tabIndex={0}
            className="md:hidden"
          >
            <p className="underline">{t(labelKey)}</p>
          </div>
          <div className="hidden md:block">
            <HoverCard openDelay={300}>
              <HoverCardTrigger asChild>
                <p className="underline">{t(labelKey)}</p>
              </HoverCardTrigger>
              <HoverCardPortal>
                <HoverCardContent
                  className="flex flex-col rounded-lg w-full ml-12 bg-white z-60"
                  align="center"
                  avoidCollisions
                >
                  {whereIsMyVinGraphic(t, src)}
                </HoverCardContent>
              </HoverCardPortal>
            </HoverCard>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        title={t("common:where_is_my_vin")}
        alignment="center"
        onClose={() => setIsModalOpen(false)}
        themeClassNames={themeClassNames}
      >
        {!isInIframe && (
          <>
            {whereIsMyVinGraphic(t, src)}
            <div className="flex flex-col mx-auto pt-6">
              <Button
                spacing="tight-hug"
                size="small"
                onClick={() => setIsModalOpen(false)}
              >
                {t("common:got_it")}
              </Button>
            </div>
          </>
        )}

        {isInIframe && <>{whereIsMyVinGraphicSmall(t, src)}</>}
      </Modal>
    </>
  );
};

export default WhereIsMyVinGraphic;
